import moment from 'moment';
import React, { useState, useRef, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import type { PropertyDefinition } from '@/reducers/pipelineReducer';

interface PropertyCellProps {
	property: PropertyDefinition;
	value: any;
	onChange: (value: any) => void;
	onBlur?: () => void;
	readOnly?: boolean;
	className?: string;
}

const PropertyCell: React.FC<PropertyCellProps> = ({
	property,
	value,
	onChange,
	onBlur,
	readOnly = false,
	className,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [localValue, setLocalValue] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing]);

	const handleSave = () => {
		onChange(localValue);
		setIsEditing(false);
		onBlur?.();
	};

	const handleCancel = () => {
		setLocalValue(value);
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSave();
		} else if (e.key === 'Escape') {
			handleCancel();
		}
	};

	// Format display value based on type
	// Click-to-edit cells also open from the keyboard (and VoiceOver).
	const editOnKey = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setIsEditing(true);
		}
	};

	const formatDisplayValue = () => {
		if (value === null || value === undefined || value === '') {
			return <span className="text-gray-400">Empty</span>;
		}

		switch (property.type) {
			case 'date':
				try {
					return moment(new Date(value)).format('ll');
				} catch {
					return value;
				}
			case 'checkbox':
				return null; // Checkbox handles its own display
			case 'number':
				return value.toString();
			default:
				return value;
		}
	};

	if (readOnly) {
		// Read-only display
		if (property.type === 'checkbox') {
			return (
				<div className={cn('flex items-center px-3', className)}>
					<Checkbox
						checked={!!value}
						disabled
						className="cursor-not-allowed"
					/>
				</div>
			);
		}
		return (
			<div className={cn('px-3 py-2 text-sm', className)}>
				{formatDisplayValue()}
			</div>
		);
	}

	// Text input cell
	if (property.type === 'text') {
		if (isEditing) {
			return (
				<input
					ref={inputRef}
					type="text"
					autoCapitalize="sentences"
					value={localValue || ''}
					onChange={(e) => setLocalValue(e.target.value)}
					onBlur={handleSave}
					onKeyDown={handleKeyDown}
					aria-label={property.name}
					enterKeyHint="done"
					className={cn(
						'w-full px-3 py-2 text-sm border-0 outline-none bg-background max-md:min-h-11 max-md:text-base touch:min-h-11',
						className
					)}
					placeholder="Empty"
				/>
			);
		}
		return (
			<div
				onClick={() => setIsEditing(true)}
				role="button"
				tabIndex={0}
				onKeyDown={editOnKey}
				className={cn(
					'w-full px-3 py-2 text-sm cursor-text hover:bg-accent transition-colors min-h-[36px] flex items-center max-md:min-h-11 touch:min-h-11',
					className
				)}
			>
				{formatDisplayValue()}
			</div>
		);
	}

	// Number input cell
	if (property.type === 'number') {
		if (isEditing) {
			return (
				<input
					ref={inputRef}
					type="number"
					inputMode="decimal"
					value={localValue || ''}
					onChange={(e) =>
						setLocalValue(
							e.target.value ? Number(e.target.value) : null
						)
					}
					onBlur={handleSave}
					onKeyDown={handleKeyDown}
					aria-label={property.name}
					enterKeyHint="done"
					className={cn(
						'w-full px-3 py-2 text-sm border-0 outline-none bg-background max-md:min-h-11 max-md:text-base touch:min-h-11',
						className
					)}
					placeholder="0"
				/>
			);
		}
		return (
			<div
				onClick={() => setIsEditing(true)}
				role="button"
				tabIndex={0}
				onKeyDown={editOnKey}
				className={cn(
					'w-full px-3 py-2 text-sm cursor-text hover:bg-accent transition-colors min-h-[36px] flex items-center max-md:min-h-11 touch:min-h-11',
					className
				)}
			>
				{formatDisplayValue()}
			</div>
		);
	}

	// Date picker cell
	if (property.type === 'date') {
		return (
			<Popover>
				<PopoverTrigger asChild>
					<button
						className={cn(
							'w-full px-3 py-2 text-sm text-left hover:bg-accent transition-colors min-h-[36px] flex items-center max-md:min-h-11 touch:min-h-11',
							!value && 'text-muted-foreground',
							className
						)}
					>
						{value
							? moment(new Date(value)).format('ll')
							: 'Empty'}
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<CalendarComponent
						mode="single"
						selected={value ? new Date(value) : undefined}
						onSelect={(date) => {
							onChange(date ? date.toISOString() : null);
						}}
						initialFocus
					/>
				</PopoverContent>
			</Popover>
		);
	}

	// Checkbox cell
	if (property.type === 'checkbox') {
		return (
			<div
				className={cn(
					'flex items-center px-3 py-2 min-h-[36px] max-md:min-h-11 touch:min-h-11',
					className
				)}
			>
				<Checkbox
					checked={!!value}
					onCheckedChange={(checked) => onChange(checked)}
					aria-label={property.name}
					className="cursor-pointer max-md:relative max-md:size-5 max-md:before:absolute max-md:before:-inset-3 touch:relative touch:size-5 touch:before:absolute touch:before:-inset-3"
				/>
			</div>
		);
	}

	// Select cell (for future implementation)
	if (property.type === 'select' && property.options) {
		if (isEditing) {
			return (
				<select
					value={localValue || ''}
					onChange={(e) => {
						setLocalValue(e.target.value);
						onChange(e.target.value);
						setIsEditing(false);
					}}
					onBlur={() => setIsEditing(false)}
					className={cn(
						'w-full px-3 py-2 text-sm border-0 outline-none bg-background',
						className
					)}
					autoFocus
				>
					<option value="">Empty</option>
					{property.options.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			);
		}
		return (
			<div
				onClick={() => setIsEditing(true)}
				role="button"
				tabIndex={0}
				onKeyDown={editOnKey}
				className={cn(
					'w-full px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors min-h-[36px] flex items-center max-md:min-h-11 touch:min-h-11',
					className
				)}
			>
				{formatDisplayValue()}
			</div>
		);
	}

	// Default fallback
	return (
		<div className={cn('px-3 py-2 text-sm', className)}>
			{formatDisplayValue()}
		</div>
	);
};

export default PropertyCell;
